import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    CheckCircleIcon,
    XCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    BanknotesIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import BookingInvoicePanel from './BookingInvoicePanel';
import { ExpandableScreen, ExpandableScreenContent } from '../ui/expandable-screen';

interface Booking {
    id: string;
    name: string;
    business_name: string | null;
    email: string;
    phone: string | null;
    event_type: string;
    event_date: string;
    start_time: string | null;
    end_time: string | null;
    location: string | null;
    notes: string | null;
    retainer: boolean;
    status: 'pending' | 'confirmed' | 'deposit_paid' | 'declined' | 'cancelled' | 'completed' | 'paid';
    admin_notes: string | null;
    total_amount_cents: number | null;
    deposit_amount_cents: number | null;
    deposit_paid_at: string | null;
    paid_at: string | null;
    created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
    pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    confirmed: 'text-green-400 bg-green-400/10 border-green-400/20',
    deposit_paid: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    declined: 'text-red-400 bg-red-400/10 border-red-400/20',
    cancelled: 'text-zinc-500 bg-zinc-500/10 border-zinc-500/20',
    completed: 'text-green-400 bg-green-400/10 border-green-400/20',
    paid: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
};

const fmtUSDc = (cents: number | null | undefined) =>
    cents == null ? '—' : `$${(cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const fmtTimestamp = (iso: string | null | undefined) =>
    !iso ? '—' : new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
];
const DAY_NAMES = ['S','M','T','W','T','F','S'];

function getAdminHeaders(): HeadersInit {
    const secret = (window as any).__ADMIN_SECRET__ || '';
    return {
        'Content-Type': 'application/json',
        'x-admin-secret': secret,
    };
}

interface BookingCardProps {
    booking: Booking;
    onStatusChange: () => void;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking: b, onStatusChange }) => {
    const [adminNotes, setAdminNotes] = useState(b.admin_notes || '');
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [invoicePanelOpen, setInvoicePanelOpen] = useState(false);

    // Billing / quote state
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [quotePrice, setQuotePrice] = useState('');
    const [depositPct, setDepositPct] = useState('50');
    const [quoteLines, setQuoteLines] = useState<{ description: string; amount: string }[]>([]);
    const [billing, setBilling] = useState(false);
    const [feedback, setFeedback] = useState<
        { type: 'success' | 'error'; msg: string; pdfUrl?: string } | null
    >(null);

    const updateStatus = async (status: string) => {
        setSaving(true);
        await fetch('/api/booking?action=admin', {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify({ id: b.id, status, admin_notes: adminNotes }),
        });
        setSaving(false);
        setExpanded(false);
        onStatusChange();
    };

    const submitQuote = async () => {
        const price = parseFloat(quotePrice);
        if (!(price > 0)) {
            setFeedback({ type: 'error', msg: 'Enter a valid project total.' });
            return;
        }
        const pct = parseFloat(depositPct);
        if (!(pct > 0) || pct >= 100) {
            setFeedback({ type: 'error', msg: 'Deposit % must be between 1 and 99.' });
            return;
        }
        const lineItems = quoteLines
            .map(l => ({ description: l.description.trim(), amount: parseFloat(l.amount) }))
            .filter(l => l.description && l.amount > 0)
            .map(l => ({ description: l.description, quantity: 1, unit_price: l.amount, amount: l.amount }));

        setBilling(true);
        setFeedback(null);
        try {
            const res = await fetch('/api/booking/create-quote', {
                method: 'POST',
                headers: getAdminHeaders(),
                body: JSON.stringify({
                    bookingId: b.id,
                    totalPrice: price,
                    depositPct: pct,
                    lineItems: lineItems.length ? lineItems : undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                setFeedback({ type: 'error', msg: data.error || data.message || `Request failed (${res.status})` });
            } else {
                setFeedback({
                    type: 'success',
                    msg: `Quote ${data.invoiceNumber} created — ${data.emailed ? 'emailed to client' : 'email failed, share the PDF manually'}.`,
                    pdfUrl: data.pdfUrl,
                });
                setQuoteOpen(false);
                setQuotePrice('');
                setQuoteLines([]);
                onStatusChange();
            }
        } catch (e: any) {
            setFeedback({ type: 'error', msg: e?.message || 'Network error' });
        } finally {
            setBilling(false);
        }
    };

    const submitFinalInvoice = async () => {
        if (!window.confirm('Generate the final invoice for the remaining balance and email it to the client?')) return;
        setBilling(true);
        setFeedback(null);
        try {
            const res = await fetch('/api/booking/create-final-invoice', {
                method: 'POST',
                headers: getAdminHeaders(),
                body: JSON.stringify({ bookingId: b.id }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                setFeedback({ type: 'error', msg: data.error || data.message || `Request failed (${res.status})` });
            } else {
                setFeedback({
                    type: 'success',
                    msg: `Invoice ${data.invoiceNumber} created — balance ${fmtUSDc(Math.round(Number(data.balanceDue) * 100))} — ${data.emailed ? 'emailed to client' : 'email failed, share the PDF manually'}.`,
                    pdfUrl: data.pdfUrl,
                });
                onStatusChange();
            }
        } catch (e: any) {
            setFeedback({ type: 'error', msg: e?.message || 'Network error' });
        } finally {
            setBilling(false);
        }
    };

    const fmtDate = (d: string) => {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric',
        });
    };

    return (
        <>
        <button data-tour="book-row"
            type="button"
            onClick={() => setExpanded(true)}
            className="w-full bg-zinc-950 hover:bg-zinc-900 transition-colors text-left p-4"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-black text-white truncate">{b.name}</span>
                        <span className={`px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest ${STATUS_COLORS[b.status]}`}>
                            {b.status}
                        </span>
                        {b.retainer && (
                            <span className="px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest bg-purple-400/10 text-purple-400">
                                Retainer
                            </span>
                        )}
                    </div>
                    <p className="text-white/50 text-xs">{b.email}</p>
                    {(b.location || b.start_time) && (
                        <div className="flex gap-4 mt-1 text-[10px] text-white/30">
                            {b.location && <span>{b.location}</span>}
                            {b.start_time && <span>{b.start_time}{b.end_time ? ` – ${b.end_time}` : ''}</span>}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                        <p className="text-xs font-bold text-white">{fmtDate(b.event_date)}</p>
                        <p className="text-white/40 text-[10px]">{b.event_type}</p>
                    </div>
                    <ChevronDownIcon className="w-4 h-4 text-white/30 -rotate-90" />
                </div>
            </div>
        </button>

        {/* Booking Detail — fullscreen ExpandableScreen pattern */}
        <ExpandableScreen isOpen={expanded} onOpenChange={setExpanded}>
            <ExpandableScreenContent className="overflow-x-hidden">
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    <div className="max-w-3xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
                        <div className="flex items-center gap-3 mb-1">
                            <span className="text-lg font-black text-white truncate">{b.name}</span>
                            <span className={`px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest ${STATUS_COLORS[b.status]}`}>
                                {b.status}
                            </span>
                        </div>
                        <p className="text-white/50 text-sm normal-case mb-8">{b.email}</p>
                        <div
                            className="space-y-4"
                        >
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div><p className="text-white/30 uppercase tracking-widest mb-0.5">Date</p><p className="font-bold">{fmtDate(b.event_date)}</p></div>
                                        <div><p className="text-white/30 uppercase tracking-widest mb-0.5">Type</p><p className="font-bold">{b.event_type}</p></div>
                                        {b.start_time && <div><p className="text-white/30 uppercase tracking-widest mb-0.5">Time</p><p className="font-bold">{b.start_time}{b.end_time ? ` – ${b.end_time}` : ''}</p></div>}
                                        {b.location && <div><p className="text-white/30 uppercase tracking-widest mb-0.5">Location</p><p className="font-bold">{b.location}</p></div>}
                                        {b.phone && <div><p className="text-white/30 uppercase tracking-widest mb-0.5">Phone</p><p className="font-bold">{b.phone}</p></div>}
                                    </div>

                                    {b.notes && (
                                        <div>
                                            <p className="text-white/30 text-[9px] uppercase tracking-widest mb-1">Client Notes</p>
                                            <p className="text-white/70 text-sm leading-relaxed bg-white/5 p-3">{b.notes}</p>
                                        </div>
                                    )}

                                    <div>
                                        <p className="text-white/30 text-[9px] uppercase tracking-widest mb-1">Admin Notes</p>
                                        <textarea
                                            value={adminNotes}
                                            onChange={e => setAdminNotes(e.target.value)}
                                            rows={2}
                                            className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none resize-none"
                                            placeholder="Internal notes..."
                                        />
                                    </div>

                                    {/* Payment state */}
                                    {(b.total_amount_cents != null || b.deposit_amount_cents != null || b.deposit_paid_at || b.paid_at) && (
                                        <div className="grid grid-cols-2 gap-3 text-xs bg-white/5 p-3">
                                            {b.total_amount_cents != null && (
                                                <div><p className="text-white/30 uppercase tracking-widest mb-0.5 text-[9px]">Quoted Total</p><p className="font-bold font-mono">{fmtUSDc(b.total_amount_cents)}</p></div>
                                            )}
                                            {b.deposit_amount_cents != null && (
                                                <div><p className="text-white/30 uppercase tracking-widest mb-0.5 text-[9px]">Deposit</p><p className="font-bold font-mono">{fmtUSDc(b.deposit_amount_cents)}</p></div>
                                            )}
                                            {b.deposit_paid_at && (
                                                <div><p className="text-white/30 uppercase tracking-widest mb-0.5 text-[9px]">Deposit Paid</p><p className="font-bold text-blue-400">{fmtTimestamp(b.deposit_paid_at)}</p></div>
                                            )}
                                            {b.paid_at && (
                                                <div><p className="text-white/30 uppercase tracking-widest mb-0.5 text-[9px]">Paid In Full</p><p className="font-bold text-emerald-400">{fmtTimestamp(b.paid_at)}</p></div>
                                            )}
                                        </div>
                                    )}

                                    {/* Billing feedback */}
                                    {feedback && (
                                        <div className={`text-xs p-3 ${feedback.type === 'success' ? 'bg-green-400/10 text-green-300' : 'bg-red-400/10 text-red-300'}`}>
                                            {feedback.msg}
                                            {feedback.pdfUrl && (
                                                <>{' '}<a href={feedback.pdfUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">View PDF</a></>
                                            )}
                                        </div>
                                    )}

                                    {/* Quote form */}
                                    {quoteOpen && (
                                        <div className="border border-white/15 p-3 space-y-3">
                                            <p className="text-white/40 text-[9px] uppercase tracking-widest">New Quote</p>
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-white/30 text-[9px] uppercase tracking-widest">Project Total (USD)</label>
                                                    <input
                                                        type="number" min="0" step="0.01"
                                                        value={quotePrice}
                                                        onChange={e => setQuotePrice(e.target.value)}
                                                        placeholder="0.00"
                                                        className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/20 focus:outline-none mt-1"
                                                    />
                                                </div>
                                                <div className="w-24">
                                                    <label className="text-white/30 text-[9px] uppercase tracking-widest">Deposit %</label>
                                                    <input
                                                        type="number" min="1" max="99"
                                                        value={depositPct}
                                                        onChange={e => setDepositPct(e.target.value)}
                                                        className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-white focus:outline-none mt-1"
                                                    />
                                                </div>
                                            </div>

                                            {quoteLines.length > 0 && (
                                                <div className="space-y-2">
                                                    {quoteLines.map((line, i) => (
                                                        <div key={i} className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={line.description}
                                                                placeholder="Line item description"
                                                                onChange={e => setQuoteLines(ls => ls.map((l, idx) => idx === i ? { ...l, description: e.target.value } : l))}
                                                                className="flex-1 bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white placeholder-white/20 focus:outline-none"
                                                            />
                                                            <input
                                                                type="number" min="0" step="0.01"
                                                                value={line.amount}
                                                                placeholder="0.00"
                                                                onChange={e => setQuoteLines(ls => ls.map((l, idx) => idx === i ? { ...l, amount: e.target.value } : l))}
                                                                className="w-24 bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white placeholder-white/20 focus:outline-none"
                                                            />
                                                            <button
                                                                onClick={() => setQuoteLines(ls => ls.filter((_, idx) => idx !== i))}
                                                                className="px-2 text-white/30 hover:text-red-400 transition-colors"
                                                            >
                                                                <TrashIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <button
                                                onClick={() => setQuoteLines(ls => [...ls, { description: '', amount: '' }])}
                                                className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors"
                                            >
                                                <PlusIcon className="w-3 h-3" /> Add line item
                                            </button>
                                            <p className="text-white/20 text-[9px] leading-relaxed">
                                                Line items are an optional breakdown shown on the quote; the deposit is calculated from the project total.
                                            </p>

                                            <div className="flex gap-2">
                                                <button
                                                    onClick={submitQuote}
                                                    disabled={billing}
                                                    className="flex-1 py-2 bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-zinc-200 transition-colors disabled:opacity-50"
                                                >
                                                    {billing ? 'Generating…' : 'Generate & Send Quote'}
                                                </button>
                                                <button
                                                    onClick={() => setQuoteOpen(false)}
                                                    disabled={billing}
                                                    className="px-4 py-2 border border-white/20 text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-white transition-colors disabled:opacity-50"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Billing actions */}
                                    {b.status !== 'declined' && b.status !== 'cancelled' && (
                                        <div className="flex gap-2 flex-wrap">
                                            {/* ── New: full invoice panel ── */}
                                            <button data-tour="book-invoice"
                                                onClick={() => setInvoicePanelOpen(true)}
                                                className="flex items-center gap-1.5 px-4 py-2 bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-zinc-200 transition-colors"
                                            >
                                                <DocumentTextIcon className="w-3.5 h-3.5" />
                                                Generate Invoice
                                            </button>
                                            <button data-tour="book-quote"
                                                onClick={() => { setQuoteOpen(o => !o); setFeedback(null); }}
                                                disabled={billing}
                                                className="flex items-center gap-1.5 px-4 py-2 bg-white/5 border border-white/15 text-white text-[10px] font-black uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50"
                                            >
                                                <DocumentTextIcon className="w-3.5 h-3.5" />
                                                {b.total_amount_cents != null ? 'Quick Quote' : 'Quick Quote'}
                                            </button>
                                            {b.status === 'deposit_paid' && (
                                                <button
                                                    onClick={submitFinalInvoice}
                                                    disabled={billing}
                                                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-blue-500 transition-colors disabled:opacity-50"
                                                >
                                                    <BanknotesIcon className="w-3.5 h-3.5" />
                                                    {billing ? 'Working…' : 'Send Final Invoice'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex gap-2 flex-wrap">
                                        <button data-tour="book-confirm"
                                            onClick={() => updateStatus('confirmed')}
                                            disabled={saving}
                                            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-green-500 transition-colors disabled:opacity-50"
                                        >
                                            <CheckCircleIcon className="w-3.5 h-3.5" /> Confirm
                                        </button>
                                        <button
                                            onClick={() => updateStatus('declined')}
                                            disabled={saving}
                                            className="flex items-center gap-1.5 px-4 py-2 bg-red-900 text-red-300 text-[10px] font-black uppercase tracking-widest hover:bg-red-800 transition-colors disabled:opacity-50"
                                        >
                                            <XCircleIcon className="w-3.5 h-3.5" /> Decline
                                        </button>
                                        <button
                                            onClick={() => updateStatus('cancelled')}
                                            disabled={saving}
                                            className="flex items-center gap-1.5 px-4 py-2 border border-white/20 text-white/40 text-[10px] font-black uppercase tracking-widest hover:border-white/40 hover:text-white/60 transition-colors disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                        </div>
                    </div>
                </div>
            </ExpandableScreenContent>
        </ExpandableScreen>

        {/* Invoice panel overlay */}
        {invoicePanelOpen && (
            <BookingInvoicePanel
                booking={b}
                onClose={() => setInvoicePanelOpen(false)}
                onInvoiceCreated={() => {
                    setInvoicePanelOpen(false);
                    onStatusChange();
                }}
            />
        )}
        </>
    );
};

const AdminBookingView: React.FC = () => {
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [blockedDates, setBlockedDates] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'bookings' | 'calendar'>('bookings');

    const today = new Date();
    const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
    const [blockNote, setBlockNote] = useState('');
    const [blockingDate, setBlockingDate] = useState<string | null>(null);

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `/api/booking?action=admin&status=${statusFilter}`,
                { headers: getAdminHeaders() }
            );
            const data = await res.json();
            setBookings(data.bookings || []);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    const fetchAvailability = useCallback(async () => {
        const res = await fetch('/api/booking?action=availability');
        const data = await res.json();
        setBlockedDates(data.blockedDates || []);
    }, []);

    useEffect(() => { fetchBookings(); }, [fetchBookings]);
    useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

    const toggleBlock = async (dateStr: string) => {
        const isBlocked = blockedDates.includes(dateStr);
        if (isBlocked) {
            await fetch('/api/booking?action=block', {
                method: 'DELETE',
                headers: getAdminHeaders(),
                body: JSON.stringify({ date: dateStr }),
            });
            fetchAvailability();
        } else {
            setBlockingDate(dateStr);
        }
    };

    const confirmBlock = async () => {
        if (!blockingDate) return;
        await fetch('/api/booking?action=block', {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ date: blockingDate, note: blockNote }),
        });
        setBlockingDate(null);
        setBlockNote('');
        fetchAvailability();
    };

    const fmtDate = (d: string) => {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day).toLocaleDateString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric',
        });
    };

    const calDays = (() => {
        const firstDay = new Date(cursor.year, cursor.month, 1).getDay();
        const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
        const cells: (number | null)[] = [];
        for (let i = 0; i < firstDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return cells;
    })();

    const fmtCell = (d: number) => {
        const mm = String(cursor.month + 1).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        return `${cursor.year}-${mm}-${dd}`;
    };

    const bookedDates = bookings
        .filter(b => ['pending', 'confirmed', 'deposit_paid', 'completed', 'paid'].includes(b.status))
        .map(b => b.event_date);

    return (
        <div className="space-y-6">
            {/* Tab switcher */}
            <div data-tour="book-tabs" className="flex gap-8 border-b border-white/10 pb-0">
                {(['bookings', 'calendar'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-3 text-[10px] font-black uppercase tracking-[0.2em] transition-colors relative ${
                            activeTab === tab ? 'text-white' : 'text-white/30 hover:text-white/60'
                        }`}
                    >
                        {tab}
                        {activeTab === tab && (
                            <motion.div layoutId="bookingTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                        )}
                    </button>
                ))}
            </div>

            {/* ── Bookings Tab ── */}
            {activeTab === 'bookings' && (
                <div className="space-y-4">
                    <div data-tour="book-filters" className="flex gap-2 flex-wrap">
                        {['all', 'pending', 'confirmed', 'declined', 'cancelled'].map(s => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-3 py-1 text-[9px] font-black uppercase tracking-widest border transition-colors ${
                                    statusFilter === s
                                        ? 'bg-white text-black border-white'
                                        : 'text-white/40 border-white/10 hover:border-white/30'
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-white/30 text-xs uppercase tracking-widest animate-pulse py-8">Loading...</div>
                    ) : bookings.length === 0 ? (
                        <div className="text-white/20 text-xs uppercase tracking-widest py-8">No bookings found.</div>
                    ) : (
                        <div className="space-y-2">
                            {bookings.map(b => (
                                <BookingCard key={b.id} booking={b} onStatusChange={fetchBookings} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Calendar Tab ── */}
            {activeTab === 'calendar' && (
                <div className="max-w-sm space-y-6">
                    <div className="flex items-center justify-between mb-2">
                        <button onClick={() => setCursor(c => ({ year: c.month === 0 ? c.year - 1 : c.year, month: c.month === 0 ? 11 : c.month - 1 }))} className="p-1 text-white/40 hover:text-white">
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="text-xs font-black uppercase tracking-[0.2em]">
                            {MONTH_NAMES[cursor.month]} {cursor.year}
                        </span>
                        <button onClick={() => setCursor(c => ({ year: c.month === 11 ? c.year + 1 : c.year, month: c.month === 11 ? 0 : c.month + 1 }))} className="p-1 text-white/40 hover:text-white">
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 mb-1">
                        {DAY_NAMES.map((d, i) => (
                            <div key={i} className="text-center text-[9px] font-black text-white/20 py-1">{d}</div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-0.5">
                        {calDays.map((d, i) => {
                            if (!d) return <div key={`e-${i}`} />;
                            const dateStr = fmtCell(d);
                            const blocked = blockedDates.includes(dateStr);
                            const booked = bookedDates.includes(dateStr);

                            return (
                                <button
                                    key={dateStr}
                                    onClick={() => toggleBlock(dateStr)}
                                    title={blocked ? 'Click to unblock' : 'Click to block'}
                                    className={`aspect-square flex items-center justify-center text-[11px] font-bold transition-all relative
                                        ${blocked ? 'bg-red-900/40 text-red-400 border border-red-900/50' : booked ? 'bg-yellow-900/30 text-yellow-400' : 'text-white/50 hover:bg-white/10'}
                                    `}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex gap-4 text-[9px] text-white/30 font-bold uppercase tracking-widest">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-900/60 border border-red-900" /> Blocked</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-yellow-900/40" /> Booked</span>
                    </div>

                    {blockingDate && (
                        <div className="border border-white/20 p-4 space-y-3">
                            <p className="text-xs font-black uppercase tracking-wider text-white">
                                Block {fmtDate(blockingDate)}?
                            </p>
                            <input
                                type="text"
                                value={blockNote}
                                onChange={e => setBlockNote(e.target.value)}
                                placeholder="Reason (optional)"
                                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-xs text-white placeholder-white/20 focus:outline-none"
                            />
                            <div className="flex gap-2">
                                <button onClick={confirmBlock} className="flex-1 py-2 bg-white text-black text-[10px] font-black uppercase tracking-widest hover:bg-zinc-200 transition-colors">
                                    Block Date
                                </button>
                                <button onClick={() => setBlockingDate(null)} className="px-4 py-2 border border-white/20 text-[10px] font-black uppercase tracking-widest text-white/50 hover:text-white transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminBookingView;
